import type { ComponentType } from 'react';
import { ArrowLeft, RotateCcw, type LucideProps } from 'lucide-react';

interface StatusAction {
  label: string;
  /**
   * A real link — e.g. `tel:+9173...` — renders as an <a>, not a <button>
   * with a JS redirect: it's the more correct and accessible way to offer
   * a phone call (screen readers announce the destination, long-press to
   * copy works on mobile). Provide this or `onClick`, not both.
   */
  href?: string;
  onClick?: () => void;
  icon?: ComponentType<LucideProps>;
}

interface PaymentStatusPanelProps {
  icon: ComponentType<LucideProps>;
  title: string;
  message: string;
  primaryAction: StatusAction;
  secondaryAction?: StatusAction;
}

/**
 * The one screen every non-happy-path payment state renders through —
 * a bad link, a decrypt/config failure, a network error, a cancelled or
 * timed-out payment. Never uses alarm-red iconography — a guest hitting a
 * payment hiccup shouldn't be met with a scary "error" visual; the message
 * itself carries whatever urgency is actually warranted, the chrome around
 * it stays calm and on-brand (see src/theme/colors.ts for the palette).
 */
export function PaymentStatusPanel({
  icon: Icon,
  title,
  message,
  primaryAction,
  secondaryAction,
}: PaymentStatusPanelProps) {
  const PrimaryIcon = primaryAction.icon ?? RotateCcw;
  const SecondaryIcon = secondaryAction?.icon ?? ArrowLeft;

  return (
    <div className="flex min-h-[80vh] items-center justify-center px-4 py-6">
      <div className="w-full max-w-[440px] rounded-2xl bg-white p-8 text-center shadow-[0_8px_28px_rgba(37,39,44,0.08)] sm:p-10">
        <div className="bg-brand-teal/15 mx-auto flex h-[72px] w-[72px] items-center justify-center rounded-full">
          <Icon className="text-brand-deep-blue h-8 w-8" strokeWidth={1.75} />
        </div>

        <h1 className="text-brand-ink mt-6 text-xl font-semibold text-balance">
          {title}
        </h1>
        <p className="text-brand-slate-blue mt-3 text-[15px] leading-relaxed">
          {message}
        </p>

        <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
          {secondaryAction && (
            <ActionButton
              variant="secondary"
              icon={SecondaryIcon}
              label={secondaryAction.label}
              href={secondaryAction.href}
              onClick={secondaryAction.onClick}
            />
          )}
          <ActionButton
            variant="primary"
            icon={PrimaryIcon}
            label={primaryAction.label}
            href={primaryAction.href}
            onClick={primaryAction.onClick}
          />
        </div>
      </div>
    </div>
  );
}

function ActionButton({
  variant,
  icon: Icon,
  label,
  href,
  onClick,
}: {
  variant: 'primary' | 'secondary';
  icon: ComponentType<LucideProps>;
  label: string;
  href?: string;
  onClick?: () => void;
}) {
  // Both hover states are derived from brand tokens via color-mix()/opacity,
  // not separate hardcoded hex values or Tailwind's generic gray scale —
  // one source of truth (src/theme/colors.ts), and the hover shade can
  // never drift out of sync with the base color. Primary darkens toward
  // black (deepens, reads as "pressed") rather than shifting to a duller,
  // grayer brand color, which read as the button fading out on hover.
  const variantClasses =
    variant === 'primary'
      ? 'bg-brand-deep-blue text-white hover:bg-[color-mix(in_srgb,var(--color-brand-deep-blue)_82%,black)]'
      : 'bg-brand-slate-blue/10 text-brand-ink border border-brand-slate-blue/25 hover:bg-brand-slate-blue/20';

  const className = `inline-flex h-12 items-center gap-2 rounded-full px-6 text-sm font-medium transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-deep-blue ${variantClasses}`;

  if (href) {
    return (
      <a href={href} className={className}>
        <Icon className="h-4 w-4" strokeWidth={2} />
        {label}
      </a>
    );
  }

  return (
    <button type="button" onClick={onClick} className={className}>
      <Icon className="h-4 w-4" strokeWidth={2} />
      {label}
    </button>
  );
}
